import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember, UpdateGuest,
  GetCardTypes, AddCardType, UpdateCardType, GetTeamByEventId,
} from '../../api/api';
import {
  fullName, normaliseText, nowISO, CARD_KINDS, needsCard, toDatetimeLocal, fromDatetimeLocal,
  fmtDateTime,
} from '../../utils/helpers';
import {
  rowFunctionIds, householdKey, extraPersonsFor, cardTally as tally, cardsByAdder, NO_ADDER,
} from '../../utils/headcount';

export const GUEST_TYPES = ['Family', 'Business'];

// The name printed on the invitation card:
//   Family   → the head of the family (with the courtesy prefix)
//   Business → the owner, with his company underneath
// A typed CardName always wins, so an unusual case can be written by hand.
export const cardNameOf = (row) => {
  if (String(row.CardName || '').trim()) return String(row.CardName).trim();
  const person = fullName(row) || row.GuestName || '';
  if (String(row.GuestType || 'Family') === 'Business') return person;
  return person;
};
export const cardSubtitleOf = (row) => {
  if (String(row.GuestType || 'Family') === 'Business') return String(row.CompanyName || '').trim();
  return String(row.Relation || row.GroupName || '').trim();
};

const typeFnIds = (t) => String(t.FunctionIds || '').split(',').map(v => v.trim()).filter(Boolean);
const csv = (v) => String(v || '').split(',').map(x => x.trim()).filter(Boolean);
const blankType = () => ({ CardTypeName: '', Kind: 'Card', FunctionIds: [], ForExtraCards: 0, ExtraFunctionIds: [] });
// Given / Pending / All for one person's round (cards AND copies) and the
// per-member shares both live in utils/headcount.js with the other counting
// rules, and are tested there - never re-implement them here.

// Names are compared without case: the API title-cases what it stores.
const same = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

const RECEPTION = /reception/i;

// Invitation cards — one per household (family head's name) or business, and
// from 2026-10-26 the screen the DISTRIBUTION is run from.
//   • It opens as YOUR round: the cards assigned to the signed-in user to
//     deliver (filter 'Given by' = me), because that is the bundle in their
//     hand. "show everyone" drops it for the visit; the dropdown shows any
//     other person's round, with their given / pending counts.
//   • Given / Pending / All for whoever is chosen.
//   • A card is marked given in one tap - it stamps the person and the MOMENT
//     (nowISO) - and both are editable afterwards, because a card handed over
//     in the morning is usually entered at night.
//   • Card types of the event (Reception card, Thread Ceremony insert …): a
//     household needs a type when invited to ANY of its functions → the ORDER
//     totals, with the extra cards added to the type marked ForExtraCards.
//   • Per card: extra cards (+ persons, counted in the Reception headcount),
//     who delivers it (a team member or a typed name), Pending / Delivered.
//   Every card field is written to all rows of the household, like CardName.
export function useCards(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanMembers');
  const canEdit = isAdmin || can('CanMembersEdit') || can('CanMembers');
  const canManageTypes = isAdmin;

  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fnFilter, setFnFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [cardTypeFilter, setCardTypeFilter] = useState('');
  // The name the signed-in user is stored under when they deliver a card.
  const myName = useMemo(() => (
    [user.Name, user.Surname].filter(Boolean).join(' ').trim() || user.MobileNumber || ''
  ), [user]);
  // WHO CARRIES the bundle. '' = everyone · '—' = not assigned yet · a name.
  // Secondary since 2026-10-29: it opens on EVERYONE, because until a bundle is
  // physically handed over `CardDeliveredByName` is null on every row, and
  // opening on a carrier filter nobody matches showed an empty, baffling screen.
  const [delivererFilter, setDelivererFilter] = useState('');
  // WHO GIVES the card, by UserId ('' = everyone). The rule of this event
  // (owner, 2026-10-29): **the member who invited a household gives it their
  // card**, so the adder IS the giver and this is the screen's main question.
  const [adderFilter, setAdderFilter] = useState('');
  // '' = all · 'single' = a card for one person · 'family' = a household.
  const [shapeFilter, setShapeFilter] = useState('');
  // '' = all · 'Delivered' (given) · 'Pending'.
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  // The search-and-give box: find a name anywhere in the event and hand that
  // card over on the spot. Deliberately NOT tied to `q` / the filters below -
  // somebody standing in front of you is given their card whoever's round it
  // is on and whatever the screen is currently showing.
  const [quickQ, setQuickQ] = useState('');
  const [editing, setEditing] = useState(null);      // the card whose text is open
  const [cardForm, setCardForm] = useState({ GuestType: 'Family', CompanyName: '', CardName: '' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [typeForm, setTypeForm] = useState(null);    // null = closed; { ...blankType(), CardTypeId? }
  // The "given by / when" editor: { card, by, when } (when = datetime-local).
  const [givenForm, setGivenForm] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader.then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load guests'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);
  const loadTypes = useCallback(() => {
    GetCardTypes({ EventId: event.EventId }).then(d => setCardTypes(Array.isArray(d) ? d : [])).catch(() => setCardTypes([]));
  }, [event]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => setFunctions([]));
    GetTeamByEventId({ EventId: event.EventId })
      .then(d => setTeam(Array.isArray(d) ? d : [])).catch(() => setTeam([]));
  }, [event]);

  // Every card (one per household), before the filters.
  const allCards = useMemo(() => {
    const byKey = new Map();
    rows.filter(needsCard).forEach(r => {
      const key = householdKey(r);
      if (!byKey.has(key)) byKey.set(key, { key, head: r, members: [], functionNames: new Set(), fnIds: new Set() });
      const card = byKey.get(key);
      // The family head (GuestId === FamilyId) is the name on the card.
      if (Number(r.FamilyId || 0) && String(r.GuestId) === String(r.FamilyId)) card.head = r;
      if (!card.members.some(m => String(m.GuestId) === String(r.GuestId))) card.members.push(r);
      rowFunctionIds(r).forEach(id => card.fnIds.add(String(id)));
      String(r.FunctionNames || r.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean)
        .forEach(n => card.functionNames.add(n));
    });
    return Array.from(byKey.values()).map(c => {
      const extraCards = c.members.reduce((s, m) => Math.max(s, Number(m.ExtraCards || 0)), 0);
      const h = c.head;
      return {
        ...c,
        name: cardNameOf(h),
        subtitle: cardSubtitleOf(h),
        // The head's own three sort keys. The printed list is read to FIND a
        // name, so it runs Group → Surname → Name like the guest export - and
        // the card's printed name may be typed over, so it cannot be split.
        group: String(h.GroupName || '').trim(),
        surname: String(h.Surname || '').trim(),
        firstName: String(h.GuestName || '').trim(),
        type: String(h.GuestType || 'Family'),
        mobile: h.MobileNumber || '',
        city: h.City || '',
        persons: c.members.length,
        functions: Array.from(c.functionNames),
        cardTypes: cardTypes.filter(t => typeFnIds(t).some(id => c.fnIds.has(id))),
        extraCards,
        extraPersonsTyped: Number(h.ExtraPersons || 0),
        extraPersons: extraPersonsFor(extraCards, h.ExtraPersons),
        deliveredByUserId: h.CardDeliveredByUserId || null,
        deliveredBy: String(h.CardDeliveredByName || '').trim(),
        status: h.CardStatus || 'Pending',
        deliveredDate: h.CardDeliveredDate || null,
        // WHO INVITED this household - a different question from who carries
        // the card, and the one that actually finds it on a big event ("it is
        // in Sanjay's list"). Keyed on CreatedBy like the Guests page.
        addedById: String(h.CreatedBy || ''),
        addedBy: String(h.AddedByName || h.InvitedByName || '').trim(),
      };
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rows, cardTypes]);

  // Whose round is on screen: nobody's (everyone's cards), the ones nobody
  // holds yet, or one person's - mine when the page opens.
  const everyone = !delivererFilter;
  const mineOnly = !!myName && same(delivererFilter, myName);
  const showEveryone = () => setDelivererFilter('');
  const showMine = () => setDelivererFilter(myName);
  // A card is mine when it carries my UserId - the name is only a fallback,
  // because the API title-cases what it stores and a team member may be saved
  // under a slightly different spelling than My Profile shows.
  const isMine = useCallback(
    (c) => String(c.deliveredByUserId || '') === String(me) || (!!myName && same(c.deliveredBy, myName)),
    [me, myName]);
  const heldBy = useCallback(
    (c, name) => (same(name, myName) ? isMine(c) : same(c.deliveredBy, name)),
    [myName, isMine]);

  // A card is for ONE person or for a HOUSEHOLD - the two are picked and
  // carried differently, so each can be listed on its own.
  const isFamilyCard = (c) => c.persons > 1;

  // Everything except the single/family choice, so that choice can be labelled
  // with what it would leave.
  const beforeShape = useMemo(() => {
    let list = allCards;
    if (fnFilter) list = list.filter(c => c.fnIds.has(String(fnFilter)));
    if (typeFilter) list = list.filter(c => c.type === typeFilter);
    if (cardTypeFilter) list = list.filter(c => c.cardTypes.some(t => String(t.CardTypeId) === String(cardTypeFilter)));
    // NO_ADDER is a real choice: the cards whose adder was never saved.
    if (adderFilter === NO_ADDER) list = list.filter(c => !c.addedById);
    else if (adderFilter) list = list.filter(c => c.addedById === String(adderFilter));
    if (delivererFilter === '—') list = list.filter(c => !c.deliveredBy);
    else if (delivererFilter) list = list.filter(c => heldBy(c, delivererFilter));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(c => `${c.name} ${c.subtitle} ${c.mobile} ${c.city} ${c.deliveredBy} ${c.addedBy}`.toLowerCase().includes(s));
    }
    return list;
  }, [allCards, fnFilter, typeFilter, cardTypeFilter, adderFilter, delivererFilter, heldBy, q]);

  // Everything except Given / Pending, so those three can be counted.
  const scoped = useMemo(() => (
    shapeFilter ? beforeShape.filter(c => (shapeFilter === 'family' ? isFamilyCard(c) : !isFamilyCard(c))) : beforeShape
  ), [beforeShape, shapeFilter]);

  const shapeCounts = useMemo(() => ({
    all: beforeShape.length,
    family: beforeShape.filter(isFamilyCard).length,
    single: beforeShape.filter(c => !isFamilyCard(c)).length,
  }), [beforeShape]);

  const cards = useMemo(
    () => (statusFilter ? scoped.filter(c => c.status === statusFilter) : scoped),
    [scoped, statusFilter]);

  // The page opens on YOUR cards to give - the households you invited - because
  // that is the bundle you are asked for by name. When you invited none it
  // opens on everyone's, and the scope line says which it is showing. It runs
  // once per load and never overrides a choice made since.
  const noneOfMine = useMemo(() => !allCards.some(isMine), [allCards, isMine]);
  const myPending = useMemo(
    () => allCards.filter(c => isMine(c) && c.status !== 'Delivered').length,
    [allCards, isMine]);
  const myAdded = useMemo(
    () => allCards.filter(c => String(c.addedById) === String(me)).length,
    [allCards, me]);
  const scopedOnce = useRef(false);
  useEffect(() => {
    if (loading || scopedOnce.current || !allCards.length) return;
    scopedOnce.current = true;
    if (myAdded) setAdderFilter(String(me));
  }, [loading, allCards, myAdded, me]);

  const counts = useMemo(() => ({
    ...tally(scoped),
    shown: cards.length,
    family: cards.filter(c => c.type === 'Family').length,
    business: cards.filter(c => c.type === 'Business').length,
  }), [scoped, cards]);

  // How many of each card to ORDER (all households, not the filtered view).
  const orderTotals = useMemo(() => cardTypes.map(t => {
    const households = allCards.filter(c => c.cardTypes.some(x => x.CardTypeId === t.CardTypeId)).length;
    const extra = Number(t.ForExtraCards) === 1 ? allCards.reduce((s, c) => s + c.extraCards, 0) : 0;
    return { type: t, households, extra, total: households + extra };
  }), [cardTypes, allCards]);
  // The hosts' own family (Guest Type 'Own family'): counted at the functions,
  // but no card - left out of every card above. Distinct people.
  const ownFamily = useMemo(() => new Set(rows.filter(r => !needsCard(r)).map(r => String(r.GuestId))).size, [rows]);
  const withoutCard = useMemo(() => (cardTypes.length ? allCards.filter(c => !c.cardTypes.length).length : 0), [cardTypes, allCards]);
  const extraTotals = useMemo(() => ({
    cards: allCards.reduce((s, c) => s + c.extraCards, 0),
    persons: allCards.reduce((s, c) => s + c.extraPersons, 0),
  }), [allCards]);

  const teamOptions = useMemo(() => team
    .map(t => ({ userId: t.UserId, name: [t.Name, t.Surname].filter(Boolean).join(' ').trim() || t.MobileNumber }))
    .filter(t => t.name)
    .sort((a, b) => a.name.localeCompare(b.name)), [team]);
  const delivererNames = useMemo(() => [...new Set([
    ...teamOptions.map(t => t.name), ...allCards.map(c => c.deliveredBy).filter(Boolean),
  ])].sort(), [teamOptions, allCards]);

  // Everyone's round, for the dropdown and the chips: the whole team (a person
  // carrying nothing yet is still pickable - the list is how a bundle is
  // handed over), plus anyone already named on a card, plus the ones nobody
  // holds. Me first: it is my own round I am looking for.
  const delivererOptions = useMemo(() => {
    // One row per person however their name was spelt when it was saved.
    const names = new Map();
    if (myName) names.set(myName.toLowerCase(), myName);
    delivererNames.forEach(n => { if (!names.has(n.toLowerCase())) names.set(n.toLowerCase(), n); });
    const rows = [...names.values()].map(name => ({
      name, label: same(name, myName) ? `${name} (me)` : name,
      ...tally(allCards.filter(c => heldBy(c, name))),
    }));
    const unassigned = allCards.filter(c => !c.deliveredBy);
    if (unassigned.length) rows.push({ name: '—', label: 'Not given to anyone yet', ...tally(unassigned) });
    return rows.sort((a, b) => (
      same(b.name, myName) - same(a.name, myName)
      || (a.name === '—') - (b.name === '—')
      || a.name.localeCompare(b.name)
    ));
  }, [delivererNames, allCards, myName, heldBy]);
  // Kept for the summary strip: who holds how many copies, how many are done.
  const byDeliverer = useMemo(
    () => delivererOptions.map(d => ({ name: d.name, cards: d.cards, delivered: d.given })),
    [delivererOptions]);

  // ── Per person who INVITED: total cards, given, still pending ────────────
  // Counted over the WHOLE event, never the filtered view: "Sanjay has 40
  // cards, 12 given" has to mean the same number whatever is on screen, and it
  // is the number the person is asked for. Keyed on CreatedBy; the name comes
  // from the AddedByName the rows were saved with.
  const adderOptions = useMemo(
    () => cardsByAdder(allCards, { meId: me, myName }),
    [allCards, me, myName]);
  const adder = useMemo(
    () => adderOptions.find(a => String(a.value) === String(adderFilter)) || null,
    [adderOptions, adderFilter]);
  // The same four numbers for EVERYBODY, so "All" is a row of the same shape
  // and the strip never has to be written twice.
  const adderAll = useMemo(() => {
    const t = tally(allCards);
    return {
      value: '', name: 'Everyone', label: 'Everyone',
      total: t.cards, given: t.given, pending: t.pending,
      households: t.cards, copies: t.copies, givenCopies: t.givenCopies,
      extraCards: t.copies - t.cards,
      families: allCards.filter(c => c.persons > 1).length,
    };
  }, [allCards]);
  // Whose numbers the strip shows: the person chosen, else everyone.
  const adderSummary = adder || adderAll;
  const addedByMe = !!adderFilter && String(adderFilter) === String(me);
  const showAllAdders = () => setAdderFilter('');
  const showMyAdded = () => setAdderFilter(String(me));

  // ── Share / Export: who is to give how many cards ───────────────────────
  // The sheet the distribution is RUN from, and the one a member is handed.
  // It follows the "Cards to give by" choice and says each thing ONCE:
  //   • one member  → just their cards. Their name and the three numbers are
  //     already in the header line, so a one-row table repeating them and a
  //     heading repeating the name are only paper.
  //   • all members → one line each, Cards / Given / Pending, with a total,
  //     and under it every card of the event.
  // The card list is read to FIND a name, so it is sorted the way the guest
  // export is: Group → Surname → Name, with **Added by** first when the list
  // is everybody's, so each card sits under the member who must give it.
  const giversReport = useMemo(() => {
    const txt = (v) => String(v || '').trim().toLowerCase();
    const blankLast = (a, b) => (a === b ? 0 : !a ? 1 : !b ? -1 : a.localeCompare(b));
    const byName = (a, b) => blankLast(txt(a.group), txt(b.group))
      || blankLast(txt(a.surname), txt(b.surname))
      || txt(a.firstName).localeCompare(txt(b.firstName))
      || txt(a.name).localeCompare(txt(b.name));
    const cardList = (list, everyone) => list
      .slice()
      .sort((a, b) => (everyone ? blankLast(txt(a.addedBy), txt(b.addedBy)) : 0) || byName(a, b))
      .map((c, i) => ({ ...c, sn: i + 1 }));
    const cardColumns = (everyone) => [
      { key: 'sn', label: '#', align: 'num' },
      ...(everyone ? [{ key: 'addedBy', label: 'Added by' }] : []),
      { key: 'group', label: 'Group' },
      { key: 'name', label: 'Name on the card' },
      { key: 'mobile', label: 'Mobile' },
      { key: 'city', label: 'City' },
      { key: 'extraCards', label: '+', align: 'num', exportValue: (c) => c.extraCards || '' },
      { key: 'given', label: 'Given', exportValue: (c) => (c.status === 'Delivered' ? (fmtDateTime(c.deliveredDate) || 'Yes') : '') },
    ];
    const head = {
      title: 'Cards to give',
      subtitle: adder ? adder.name : 'All members',
      summary: [
        { label: 'Cards', value: adderSummary.total },
        { label: 'Given', value: adderSummary.given },
        { label: 'Pending', value: adderSummary.pending },
      ],
    };
    if (adder) {
      return {
        ...head,
        columns: cardColumns(false),
        rows: cardList(allCards.filter(c => (adder.noAdder ? !c.addedById : c.addedById === String(adder.value))), false),
      };
    }
    const list = adderOptions.map((r, i) => ({ ...r, sn: i + 1 }));
    const sum = (k) => list.reduce((t, r) => t + (Number(r[k]) || 0), 0);
    return {
      ...head,
      sections: [
        {
          title: 'Cards by the member who invited them',
          columns: [
            { key: 'sn', label: '#', align: 'num' },
            { key: 'name', label: 'Member', exportValue: (r) => r.name },
            { key: 'total', label: 'Cards', align: 'num' },
            { key: 'given', label: 'Given', align: 'num' },
            { key: 'pending', label: 'Pending', align: 'num' },
            // Copies to ORDER, never more to hand over. Dropped when all blank.
            { key: 'extraCards', label: '+', align: 'num', exportValue: (r) => r.extraCards || '' },
          ],
          rows: list,
          totalsRow: {
            name: 'Total', total: sum('total'), given: sum('given'),
            pending: sum('pending'), extraCards: sum('extraCards') || '',
          },
        },
        { title: 'The cards', columns: cardColumns(true), rows: cardList(allCards, true) },
      ],
    };
  }, [adder, adderOptions, adderSummary, allCards]);

  // ── Search a name, give that card ───────────────────────────────────────
  // The move this screen exists for: somebody is in front of you, you type two
  // letters of their name and hand the card over. It looks through EVERY card
  // of the event (not the filtered list) because the person standing there does
  // not know whose round they are on.
  const quickMatches = useMemo(() => {
    const s = quickQ.trim().toLowerCase();
    if (s.length < 2) return [];
    return allCards
      .filter(c => `${c.name} ${c.subtitle} ${c.mobile} ${c.city}`.toLowerCase().includes(s))
      .slice(0, 8);
  }, [allCards, quickQ]);
  const clearQuick = () => setQuickQ('');

  // ── Writes: one household = all its rows ─────────────────────────────────
  const writeCards = async (list, data, okMsg) => {
    const ids = list.flatMap(c => c.members.map(m => Number(m.GuestId))).filter(Boolean);
    if (!ids.length) return;
    try {
      await UpdateGuest({ data: { ...data, UpdatedBy: me }, where: `GuestId in (${ids.join(',')})` });
      setRows(rs => rs.map(r => (ids.includes(Number(r.GuestId)) ? { ...r, ...data } : r)));
      if (okMsg) toast(okMsg);
    } catch {
      toast('Could not save - check your connection');
    }
  };

  const setExtraCards = (card, value) => {
    const n = Math.max(0, Math.min(99, Number(value) || 0));
    if (n === card.extraCards) return;
    writeCards([card], { ExtraCards: n, ...(n ? {} : { ExtraPersons: null }) });
  };
  const setExtraPersons = (card, value) => {
    const n = Math.max(0, Math.min(999, Number(value) || 0));
    if (n === card.extraPersonsTyped) return;
    writeCards([card], { ExtraPersons: n || null });
  };
  // value: a team member's name (then their UserId is kept too), a typed name, or '' to clear.
  const deliveryData = (value) => {
    const name = normaliseText(String(value || '')).trim();
    if (!name) return { CardDeliveredByUserId: null, CardDeliveredByName: null };
    const member = teamOptions.find(t => t.name.toLowerCase() === name.toLowerCase());
    return { CardDeliveredByUserId: member ? member.userId : null, CardDeliveredByName: member ? member.name : name };
  };
  const setDeliverer = (card, value) => {
    if (normaliseText(String(value || '')).trim() === card.deliveredBy) return;
    writeCards([card], deliveryData(value));
  };
  // ── Given: who handed it over, and when ──────────────────────────────────
  // Marking a card given stamps the moment. Whoever is carrying it keeps the
  // credit; an unassigned card is credited to the person doing the marking.
  const givenData = (name, when) => ({
    ...deliveryData(name),
    CardStatus: 'Delivered',
    CardDeliveredDate: when || nowISO(),
  });
  const pendingData = { CardStatus: 'Pending', CardDeliveredDate: null };
  const markGiven = (card) => writeCards([card], givenData(card.deliveredBy || myName));
  const markPending = (card) => writeCards([card], pendingData);
  const toggleGiven = (card) => (card.status === 'Delivered' ? markPending(card) : markGiven(card));
  // Found by name in the quick box and handed over: marked given by whoever is
  // carrying it (me, if nobody is), and the box clears ready for the next
  // person in the queue.
  const quickGive = async (card) => {
    await writeCards([card], givenData(card.deliveredBy || myName), `Given to ${card.name}`);
    setQuickQ('');
  };
  const setStatus = (card, status) => (status === 'Delivered' ? markGiven(card) : markPending(card));

  // The editor behind the date: the person and the moment, both typed over.
  const openGiven = (card) => setGivenForm({
    card,
    by: card.deliveredBy || myName,
    when: toDatetimeLocal(card.deliveredDate) || toDatetimeLocal(nowISO()),
  });
  const closeGiven = () => setGivenForm(null);
  const setGivenF = (k, v) => setGivenForm(f => ({ ...f, [k]: k === 'by' ? normaliseText(v) : v }));
  const givenNow = () => setGivenForm(f => ({ ...f, when: toDatetimeLocal(nowISO()) }));
  const saveGiven = async () => {
    if (!givenForm) return;
    setSaving(true);
    await writeCards([givenForm.card], givenData(givenForm.by, fromDatetimeLocal(givenForm.when)), 'Card given');
    setSaving(false);
    setGivenForm(null);
  };
  // "Not given after all" - the card goes back on the pending pile, the
  // carrier keeps it.
  const unGive = async () => {
    if (!givenForm) return;
    setSaving(true);
    await writeCards([givenForm.card], pendingData, 'Back to pending');
    setSaving(false);
    setGivenForm(null);
  };

  // Bulk: tick cards, then give them all to one person / mark them delivered.
  const toggleSelect = (key) => setSelected(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const allVisibleSelected = cards.length > 0 && cards.every(c => selected.has(c.key));
  const toggleSelectAll = () => setSelected(s => {
    const n = new Set(s);
    if (allVisibleSelected) cards.forEach(c => n.delete(c.key)); else cards.forEach(c => n.add(c.key));
    return n;
  });
  const selectedCards = useMemo(() => allCards.filter(c => selected.has(c.key)), [allCards, selected]);
  // Hand a bundle to somebody: they are now CARRYING these cards. It says
  // nothing about whether a guest has received one - that is bulkGiveNow.
  const bulkDeliverer = async (value) => {
    if (!selectedCards.length) return;
    const name = normaliseText(String(value || '')).trim();
    await writeCards(selectedCards, deliveryData(value),
      name ? `${selectedCards.length} cards are now with ${name}` : `${selectedCards.length} cards are with nobody`);
    setSelected(new Set());
  };
  // The commonest move on a phone: pick cards off the pile and walk out with them.
  const bulkTake = () => bulkDeliverer(myName);
  const bulkStatus = async (status) => {
    if (!selectedCards.length) return;
    await writeCards(selectedCards, status === 'Delivered' ? givenData(myName) : pendingData,
      `${selectedCards.length} cards marked ${status === 'Delivered' ? 'given' : 'pending'}`);
    setSelected(new Set());
  };
  // The round just walked: all the ticked cards, given by me, now.
  const bulkGiveNow = () => bulkStatus('Delivered');

  // ── The card text (name / business) ──────────────────────────────────────
  const openCard = (card) => {
    setEditing(card);
    setCardForm({
      GuestType: card.head.GuestType || 'Family',
      CompanyName: card.head.CompanyName || '',
      CardName: card.head.CardName || '',
    });
  };
  const setCardF = (k, v) => setCardForm(f => ({ ...f, [k]: ['CompanyName', 'CardName'].includes(k) ? normaliseText(v) : v }));
  const saveCard = async () => {
    if (!editing) return;
    setSaving(true);
    await writeCards([editing], { ...cardForm }, 'Card updated');
    setSaving(false);
    setEditing(null);
  };

  // ── Card types (admins) ──────────────────────────────────────────────────
  const openTypeAdd = () => setTypeForm(blankType());
  const openTypeEdit = (t) => setTypeForm({
    CardTypeId: t.CardTypeId, CardTypeName: t.CardTypeName || '', Kind: t.Kind || 'Card',
    FunctionIds: typeFnIds(t), ForExtraCards: Number(t.ForExtraCards) === 1 ? 1 : 0, ExtraFunctionIds: csv(t.ExtraFunctionIds),
  });
  const closeType = () => setTypeForm(null);
  const setTypeF = (k, v) => setTypeForm(f => ({ ...f, [k]: k === 'CardTypeName' ? normaliseText(v) : v }));
  const toggleTypeFunction = (id) => setTypeForm(f => {
    const s = String(id);
    const FunctionIds = f.FunctionIds.includes(s) ? f.FunctionIds.filter(x => x !== s) : [...f.FunctionIds, s];
    return { ...f, FunctionIds, ExtraFunctionIds: f.ExtraFunctionIds.filter(x => FunctionIds.includes(x)) };
  });
  // Which of the card's functions the extra-card guests come to (Reception).
  const toggleTypeExtraFunction = (id) => setTypeForm(f => {
    const s = String(id);
    return { ...f, ExtraFunctionIds: f.ExtraFunctionIds.includes(s) ? f.ExtraFunctionIds.filter(x => x !== s) : [...f.ExtraFunctionIds, s] };
  });
  const saveType = async () => {
    const f = typeForm;
    if (!String(f.CardTypeName).trim()) { toast('Give the card a name'); return; }
    if (!f.FunctionIds.length) { toast('Tick the function(s) this card is for'); return; }
    if (f.ForExtraCards && !f.ExtraFunctionIds.length) { toast('Tick the function(s) extra-card guests come to'); return; }
    const data = {
      CardTypeName: f.CardTypeName.trim(), Kind: f.Kind, FunctionIds: f.FunctionIds.join(','),
      ForExtraCards: f.ForExtraCards ? 1 : 0,
      ExtraFunctionIds: f.ForExtraCards ? f.ExtraFunctionIds.join(',') : null, UpdatedBy: me,
    };
    try {
      // Only one type can carry the extra cards.
      if (data.ForExtraCards) {
        const others = cardTypes.filter(t => Number(t.ForExtraCards) === 1 && t.CardTypeId !== f.CardTypeId);
        await Promise.all(others.map(t => UpdateCardType({ data: { ForExtraCards: 0, UpdatedBy: me }, where: `CardTypeId = ${Number(t.CardTypeId)}` })));
      }
      if (f.CardTypeId) {
        await UpdateCardType({ data, where: `CardTypeId = ${Number(f.CardTypeId)}` });
      } else {
        await AddCardType({ ...data, EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: cardTypes.length + 1, Valid: 1, CreatedBy: me });
      }
      toast('Card type saved'); setTypeForm(null); loadTypes();
    } catch { toast('Could not save the card type'); }
  };
  const deleteType = async (t) => {
    if (!window.confirm(`Remove the card type "${t.CardTypeName}"? Guests are not affected.`)) return;
    try {
      await UpdateCardType({ data: { Valid: 0, UpdatedBy: me }, where: `CardTypeId = ${Number(t.CardTypeId)}` });
      loadTypes();
    } catch { toast('Could not remove'); }
  };
  // One click for the usual set-up: the Reception card for every function
  // except the thread ceremony, carrying the extra cards - whose guests come to
  // the Reception only (else the last function) - plus a Thread Ceremony insert
  // when the event has one.
  const thread = /thread|janeu|janoi|upanayan|munj/i;
  const addDefaultTypes = async () => {
    const isThread = (f) => thread.test(`${f.FunctionType || ''} ${f.FunctionTypeName || ''} ${f.FunctionName || ''}`);
    const main = functions.filter(f => !isThread(f)).map(f => String(f.FunctionId));
    const ins = functions.filter(isThread).map(f => String(f.FunctionId));
    const rec = functions.filter(f => !isThread(f) && RECEPTION.test([f.FunctionType, f.FunctionTypeName, f.FunctionName].join(' ')))
      .map(f => String(f.FunctionId));
    const extraIds = rec.length ? rec : main.slice(-1);
    try {
      if (main.length) await AddCardType({ CardTypeName: 'Reception Card', Kind: 'Card', FunctionIds: main.join(','), ForExtraCards: 1, ExtraFunctionIds: extraIds.join(','), EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: 1, Valid: 1, CreatedBy: me, UpdatedBy: me });
      if (ins.length) await AddCardType({ CardTypeName: 'Thread Ceremony Insert', Kind: 'Insert', FunctionIds: ins.join(','), ForExtraCards: 0, EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: 2, Valid: 1, CreatedBy: me, UpdatedBy: me });
      toast('Card types added - check their functions'); loadTypes();
    } catch { toast('Could not add the card types'); }
  };

  const print = () => window.print();
  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '', [functions]);

  return {
    canView, canEdit, canManageTypes, cards, counts, loading, functions, event, fnName, refresh,
    fnFilter, setFnFilter, typeFilter, setTypeFilter, cardTypeFilter, setCardTypeFilter,
    delivererFilter, setDelivererFilter, statusFilter, setStatusFilter, q, setQ,
    adderFilter, setAdderFilter, adderOptions, adder, adderAll, adderSummary,
    addedByMe, myAdded, showAllAdders, showMyAdded, shapeFilter, setShapeFilter, shapeCounts,
    quickQ, setQuickQ, quickMatches, quickGive, clearQuick, giversReport,
    myName, everyone, mineOnly, showEveryone, showMine, delivererOptions, noneOfMine, myPending,
    cardTypes, orderTotals, withoutCard, ownFamily, extraTotals, byDeliverer, teamOptions, delivererNames,
    setExtraCards, setExtraPersons, setDeliverer, setStatus,
    markGiven, markPending, toggleGiven,
    givenForm, openGiven, closeGiven, setGivenF, givenNow, saveGiven, unGive,
    selected, toggleSelect, allVisibleSelected, toggleSelectAll, selectedCount: selectedCards.length, bulkDeliverer, bulkTake, bulkStatus, bulkGiveNow,
    editing, setEditing, cardForm, setCardF, saveCard, saving, openCard,
    typeForm, openTypeAdd, openTypeEdit, closeType, setTypeF, toggleTypeFunction, toggleTypeExtraFunction, saveType, deleteType, addDefaultTypes,
    CARD_KINDS, typeFnIds, print,
  };
}
